import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell,
  TableBody, TableContainer, CircularProgress, IconButton, TextField,
  InputAdornment, Alert, Tooltip, Button, Chip, Collapse, ButtonGroup,
} from "@mui/material";
import ArrowBackIcon      from "@mui/icons-material/ArrowBack";
import SearchIcon         from "@mui/icons-material/Search";
import MeetingRoomIcon    from "@mui/icons-material/MeetingRoom";
import Inventory2Icon     from "@mui/icons-material/Inventory2";
import CheckCircleIcon    from "@mui/icons-material/CheckCircle";
import WarningAmberIcon   from "@mui/icons-material/WarningAmber";
import FilterAltIcon      from "@mui/icons-material/FilterAlt";
import PictureAsPdfIcon   from "@mui/icons-material/PictureAsPdf";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import LayersIcon          from "@mui/icons-material/Layers";
import TableRowsIcon       from "@mui/icons-material/TableRows";
import UnfoldMoreIcon      from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon      from "@mui/icons-material/UnfoldLess";
import VisibilityIcon      from "@mui/icons-material/Visibility";
import SwapHorizIcon       from "@mui/icons-material/SwapHoriz";
import api from "../../../services/api";

const C = {
  primary:   "#2563EB",
  primaryBg: "#EFF6FF",
  surface:   "#FFFFFF",
  surfaceAlt:"#F8FAFC",
  border:    "#E2E8F0",
  text1:     "#0F172A",
  text2:     "#475569",
  text3:     "#94A3B8",
  success:   "#16A34A",
  successBg: "#F0FDF4",
  successBdr:"#BBF7D0",
  warn:      "#D97706",
  warnBg:    "#FFFBEB",
  warnBdr:   "#FDE68A",
  danger:    "#DC2626",
  dangerBg:  "#FEF2F2",
  dangerBdr: "#FECACA",
  infoBdr:   "#BFDBFE",
  neutral:   "#64748B",
  neutralBg: "#F1F5F9",
  neutralBdr:"#CBD5E1",
};

const CONDITION_MAP = {
  baik:         { label: "Baik",         color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  cukup_baik:   { label: "Cukup Baik",   color: C.primary, bg: C.primaryBg, border: C.infoBdr,    dot: "#3B82F6" },
  rusak_ringan: { label: "Rusak Ringan",  color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  rusak_berat:  { label: "Rusak Berat",   color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};
const STATUS_MAP = {
  aktif:           { label: "Aktif",           color: C.success, bg: C.successBg, border: C.successBdr, dot: "#22C55E" },
  tidak_aktif:     { label: "Tidak Aktif",     color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" },
  dipinjam:        { label: "Dipinjam",        color: C.warn,    bg: C.warnBg,    border: C.warnBdr,    dot: "#F59E0B" },
  dalam_perbaikan: { label: "Dalam Perbaikan", color: C.danger,  bg: C.dangerBg,  border: C.dangerBdr,  dot: "#EF4444" },
};

function StatusChip({ map, value, label }) {
  const cfg = map[value] || { label: value || "—", color: C.neutral, bg: C.neutralBg, border: C.neutralBdr, dot: "#94A3B8" };
  const displayLabel = label || cfg.label;
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", backgroundColor: cfg.bg,
      border: `1px solid ${cfg.border}`, borderRadius: "6px" }}>
      <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: cfg.dot, flexShrink: 0 }} />
      <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: cfg.color, lineHeight: 1 }}>
        {displayLabel}
      </Typography>
    </Box>
  );
}


function formatDate(dateStr) {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" });
}

const thSx = {
  fontSize: "0.72rem", fontWeight: 700, color: C.text3,
  borderBottom: `1px solid ${C.border}`,
  py: "10px", px: "12px",
  textTransform: "uppercase", letterSpacing: "0.04em",
  whiteSpace: "nowrap", backgroundColor: "transparent",
};
const tdSx = {
  py: "10px", px: "12px",
  borderBottom: `1px solid ${C.border}`,
  verticalAlign: "middle",
};

function StatCard({ label, value, color, bg, border, icon }) {
  return (
    <Box sx={{
      flex: "1 1 130px",
      backgroundColor: bg,
      border: `1px solid ${border}`,
      borderRadius: "12px",
      p: "16px 20px",
      display: "flex", alignItems: "center", gap: 1.5,
    }}>
      <Box sx={{ width: 36, height: 36, borderRadius: "9px", backgroundColor: C.surface,
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        boxShadow: "0 1px 3px rgba(0,0,0,0.06)" }}>
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: "1.5rem", fontWeight: 800, color, lineHeight: 1, letterSpacing: "-0.03em" }}>
          {value}
        </Typography>
        <Typography sx={{ fontSize: "0.72rem", color: C.text3, mt: 0.3, fontWeight: 500 }}>
          {label}
        </Typography>
      </Box>
    </Box>
  );
}

export default function RoomDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [room,        setRoom]        = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState("");
  const [search,      setSearch]      = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");
  const [exporting,      setExporting]      = useState(false);
  const [viewMode,       setViewMode]       = useState("grouped"); // "grouped" | "flat"
  const [expandedGroups, setExpandedGroups] = useState({});

  const fetchRoom = useCallback(async (from, to) => {
    try {
      setLoading(true);
      const params = {};
      if (from) params.date_from = from;
      if (to)   params.date_to   = to;
      const res = await api.get(`/rooms/${id}`, { params });
      setRoom(res.data?.data ?? res.data);
    } catch {
      setError("Gagal memuat data ruangan.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRoom("", "");
  }, [fetchRoom]);

  const handleFilter = () => fetchRoom(dateFrom, dateTo);

  const handleResetFilter = () => {
    setDateFrom("");
    setDateTo("");
    fetchRoom("", "");
  };

  const handleExportPdf = async () => {
    try {
      setExporting(true);
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;

      const res = await api.get(`/rooms/${id}/export-pdf`, {
        params,
        responseType: "blob",
      });

      const url      = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link     = document.createElement("a");
      link.href      = url;
      link.download  = `inventaris-${room?.code ?? id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("Gagal mengekspor PDF. Silakan coba lagi.");
    } finally {
      setExporting(false);
    }
  };

  if (loading) return (
    <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: 400, gap: 2 }}>
      <CircularProgress size={32} thickness={4} sx={{ color: C.primary }} />
      <Typography sx={{ fontSize: "0.82rem", color: C.text3 }}>Memuat data ruangan...</Typography>
    </Box>
  );

  if (error || !room) return (
    <Box sx={{ py: 6 }}>
      <Alert severity="error" sx={{ borderRadius: "8px", border: `1px solid ${C.dangerBdr}` }}>
        {error || "Data tidak ditemukan."}
      </Alert>
    </Box>
  );

  const items    = room.items || [];
  const filtered = search.trim()
    ? items.filter(i =>
        i.name?.toLowerCase().includes(search.toLowerCase()) ||
        i.serial_number?.toLowerCase().includes(search.toLowerCase()) ||
        i.category?.toLowerCase().includes(search.toLowerCase()) ||
        i.specification?.toLowerCase().includes(search.toLowerCase())
      )
    : items;

  const sortSerial = (a, b) => {
    const sa = a.serial_number || "";
    const sb = b.serial_number || "";
    return sa.length - sb.length || sa.localeCompare(sb, undefined, { numeric: true, sensitivity: "base" });
  };

  const sortedFiltered = [...filtered].sort(sortSerial);

  // Kelompokkan barang berdasarkan nama barang
  const groupedItems = Object.values(
    sortedFiltered.reduce((acc, item) => {
      const name = item.name || "Tanpa Nama";
      if (!acc[name]) {
        acc[name] = {
          name,
          category: item.category,
          unit: item.unit || "Unit",
          items: [],
          conditions: {},
          statuses: {},
          latest_date: item.latest_movement?.moved_at || item.purchase_date || item.created_at,
        };
      }
      acc[name].items.push(item);
      const cond = item.condition || "baik";
      acc[name].conditions[cond] = (acc[name].conditions[cond] || 0) + 1;
      const stat = item.status || "aktif";
      acc[name].statuses[stat] = (acc[name].statuses[stat] || 0) + 1;
      return acc;
    }, {})
  ).map(g => {
    g.items.sort(sortSerial);
    return {
      ...g,
      total_quantity: g.items.length,
      first_serial: g.items[0]?.serial_number,
      last_serial: g.items[g.items.length - 1]?.serial_number,
    };
  });

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({ ...prev, [groupName]: !prev[groupName] }));
  };

  const toggleExpandAll = () => {
    const allExpanded = groupedItems.length > 0 && groupedItems.every(g => expandedGroups[g.name]);
    const nextState = {};
    groupedItems.forEach(g => { nextState[g.name] = !allExpanded; });
    setExpandedGroups(nextState);
  };

  const countByCondition = (c) => items.filter(i => i.condition?.toLowerCase() === c).length;

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#F8FAFC", p: { xs: 2, md: 3 } }}>

      {/* ── Header ── */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <IconButton onClick={() => navigate(-1)} size="small" sx={{
            border: `1px solid ${C.border}`, borderRadius: "8px", width: 32, height: 32,
            color: C.text2, backgroundColor: C.surface,
            "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
            transition: "all 0.15s",
          }}>
            <ArrowBackIcon sx={{ fontSize: 16 }} />
          </IconButton>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", backgroundColor: C.primaryBg,
              display: "flex", alignItems: "center", justifyContent: "center" }}>
              <MeetingRoomIcon sx={{ color: C.primary, fontSize: 20 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                <Typography sx={{ fontSize: "1.15rem", fontWeight: 700, color: C.text1, lineHeight: 1.2 }}>
                  {room.name}
                </Typography>
                <Box sx={{
                  display: "inline-flex", alignItems: "center",
                  px: "8px", py: "3px",
                  backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
                  borderRadius: "6px",
                }}>
                  <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.primary,
                    fontFamily: "'JetBrains Mono', monospace", letterSpacing: "0.04em" }}>
                    {room.code}
                  </Typography>
                </Box>
              </Box>
              <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.25 }}>
                {room.description || "Tidak ada deskripsi"}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* ── Tombol Export PDF ── */}
        <Button
          variant="contained"
          size="small"
          startIcon={<PictureAsPdfIcon sx={{ fontSize: 16 }} />}
          onClick={handleExportPdf}
          disabled={exporting}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontSize: "0.82rem",
            fontWeight: 600,
            px: 2,
            py: "6px",
            boxShadow: "none",
            backgroundColor: "#DC2626",
            "&:hover": { backgroundColor: "#B91C1C", boxShadow: "none" },
            "&:disabled": { backgroundColor: C.neutralBg, color: C.text3 },
          }}
        >
          {exporting ? "Mengekspor..." : "Export PDF"}
        </Button>
      </Box>

      {/* ── Stat Cards ── */}
      <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
        <StatCard label="Total Barang" value={items.length}
          color={C.primary} bg={C.primaryBg} border={C.infoBdr}
          icon={<Inventory2Icon sx={{ fontSize: 18, color: C.primary }} />} />
        <StatCard label="Kondisi Baik" value={countByCondition("baik")}
          color={C.success} bg={C.successBg} border={C.successBdr}
          icon={<CheckCircleIcon sx={{ fontSize: 18, color: C.success }} />} />
        <StatCard label="Rusak Ringan" value={countByCondition("rusak_ringan")}
          color={C.warn} bg={C.warnBg} border={C.warnBdr}
          icon={<WarningAmberIcon sx={{ fontSize: 18, color: C.warn }} />} />
        <StatCard label="Rusak Berat" value={countByCondition("rusak_berat")}
          color={C.danger} bg={C.dangerBg} border={C.dangerBdr}
          icon={<WarningAmberIcon sx={{ fontSize: 18, color: C.danger }} />} />
      </Box>

      {/* ── Filter Tanggal ── */}
      <Paper elevation={0} sx={{
        border: `1px solid ${C.border}`, borderRadius: "12px",
        p: "12px 16px", mb: 2,
        display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap",
        backgroundColor: C.surface,
      }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <FilterAltIcon sx={{ fontSize: 16, color: C.primary }} />
          <Typography sx={{ fontSize: "0.82rem", fontWeight: 600, color: C.text2 }}>
            Filter Tanggal Masuk
          </Typography>
        </Box>

        <TextField
          type="date" size="small" label="Dari"
          value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: 160,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px", fontSize: "0.82rem",
              "& fieldset": { borderColor: C.border },
              "&:hover fieldset": { borderColor: C.primary },
              "&.Mui-focused fieldset": { borderColor: C.primary },
            },
          }}
        />

        <TextField
          type="date" size="small" label="Sampai"
          value={dateTo} onChange={(e) => setDateTo(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{
            width: 160,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px", fontSize: "0.82rem",
              "& fieldset": { borderColor: C.border },
              "&:hover fieldset": { borderColor: C.primary },
              "&.Mui-focused fieldset": { borderColor: C.primary },
            },
          }}
        />

        <Button
          variant="contained" size="small"
          onClick={handleFilter}
          sx={{
            borderRadius: "8px", textTransform: "none",
            fontSize: "0.82rem", fontWeight: 600,
            px: 2, boxShadow: "none",
            backgroundColor: C.primary,
            "&:hover": { backgroundColor: "#1D4ED8" },
          }}
        >
          Terapkan
        </Button>

        {(dateFrom || dateTo) && (
          <Button
            variant="outlined" size="small"
            onClick={handleResetFilter}
            sx={{
              borderRadius: "8px", textTransform: "none",
              fontSize: "0.82rem", fontWeight: 600,
              px: 2, borderColor: C.border, color: C.text2,
              "&:hover": { borderColor: C.primary, color: C.primary, backgroundColor: C.primaryBg },
            }}
          >
            Reset
          </Button>
        )}

        {(dateFrom || dateTo) && (
          <Typography sx={{ fontSize: "0.75rem", color: C.text3, ml: "auto" }}>
            Menampilkan barang masuk
            {dateFrom ? ` dari ${new Date(dateFrom).toLocaleDateString("id-ID", { day:"2-digit", month:"short", year:"numeric" })}` : ""}
            {dateTo   ? ` s/d ${new Date(dateTo).toLocaleDateString("id-ID",   { day:"2-digit", month:"short", year:"numeric" })}` : ""}
          </Typography>
        )}
      </Paper>

      {/* ── Table ── */}
      <Paper elevation={0} sx={{ border: `1px solid ${C.border}`, borderRadius: "12px", overflow: "hidden" }}>
        <Box sx={{ px: 2, py: "11px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 2, flexWrap: "wrap", backgroundColor: C.surfaceAlt }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Inventory2Icon sx={{ fontSize: 16, color: C.primary }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: C.text1 }}>
              Daftar Barang
            </Typography>
            <Box sx={{
              display: "inline-flex", alignItems: "center",
              px: "7px", height: 22,
              backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`,
              borderRadius: "6px",
            }}>
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 700, color: C.primary }}>
                {viewMode === "grouped" ? `${groupedItems.length} jenis barang (${filtered.length} total unit)` : `${filtered.length} item`}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            {/* View Mode Switcher */}
            <ButtonGroup size="small" sx={{ borderRadius: "8px", overflow: "hidden" }}>
              <Tooltip title="Kelompokkan barang yang sama (klik nama barang untuk lihat serial number)" arrow>
                <Button
                  variant={viewMode === "grouped" ? "contained" : "outlined"}
                  onClick={() => setViewMode("grouped")}
                  startIcon={<LayersIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.74rem", fontWeight: 600, height: 32, px: 1.2,
                    backgroundColor: viewMode === "grouped" ? C.primary : "transparent",
                    color: viewMode === "grouped" ? "#fff" : C.text2,
                    borderColor: C.border,
                    "&:hover": { backgroundColor: viewMode === "grouped" ? "#1D4ED8" : C.surfaceAlt },
                  }}>
                  Dikelompokkan
                </Button>
              </Tooltip>
              <Tooltip title="Tampilkan semua baris serial number" arrow>
                <Button
                  variant={viewMode === "flat" ? "contained" : "outlined"}
                  onClick={() => setViewMode("flat")}
                  startIcon={<TableRowsIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.74rem", fontWeight: 600, height: 32, px: 1.2,
                    backgroundColor: viewMode === "flat" ? C.primary : "transparent",
                    color: viewMode === "flat" ? "#fff" : C.text2,
                    borderColor: C.border,
                    "&:hover": { backgroundColor: viewMode === "flat" ? "#1D4ED8" : C.surfaceAlt },
                  }}>
                  Semua Baris
                </Button>
              </Tooltip>
            </ButtonGroup>

            {/* Buka Semua / Tutup Semua (Grouped Mode Only) */}
            {viewMode === "grouped" && groupedItems.length > 0 && (
              <Tooltip title="Buka atau tutup semua daftar serial number" arrow>
                <Button size="small" onClick={toggleExpandAll}
                  startIcon={groupedItems.every(g => expandedGroups[g.name])
                    ? <UnfoldLessIcon sx={{ fontSize: "14px !important" }} />
                    : <UnfoldMoreIcon sx={{ fontSize: "14px !important" }} />}
                  sx={{
                    textTransform: "none", fontSize: "0.72rem", color: C.text2, fontWeight: 600,
                    height: 32, px: 1.2, border: `1px solid ${C.border}`, borderRadius: "8px",
                    backgroundColor: C.surface,
                    "&:hover": { backgroundColor: C.surfaceAlt, borderColor: C.borderHov },
                  }}>
                  {groupedItems.every(g => expandedGroups[g.name]) ? "Tutup Semua" : "Buka Semua"}
                </Button>
              </Tooltip>
            )}

            <TextField size="small" placeholder="Cari nama, serial, kategori..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 15, color: C.text3 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 220,
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px", fontSize: "0.82rem", backgroundColor: C.surface, height: 32,
                  "& fieldset": { borderColor: C.border },
                  "&:hover fieldset": { borderColor: C.primary },
                  "&.Mui-focused fieldset": { borderColor: C.primary, borderWidth: "1.5px" },
                },
              }}
            />
          </Box>
        </Box>

        <Box sx={{ overflowX: "auto" }}>
          <Table sx={{ minWidth: 700 }}>
            <TableHead>
              <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                {viewMode === "grouped" ? (
                  <>
                    <TableCell sx={{ ...thSx, width: 60 }}>#</TableCell>
                    <TableCell sx={thSx}>Nama Barang</TableCell>
                    <TableCell sx={{ ...thSx, width: 110 }}>Total Unit</TableCell>
                    <TableCell sx={thSx}>Rentang Serial Number</TableCell>
                    <TableCell sx={thSx}>Kondisi</TableCell>
                    <TableCell sx={thSx}>Status</TableCell>
                    <TableCell sx={thSx}>Tgl. Masuk Terakhir</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right", pr: 2 }}>Serial Number</TableCell>
                  </>
                ) : (
                  ["No", "Serial Number", "Nama Barang", "Qty", "Kondisi", "Status", "Tgl. Masuk"].map(h => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))
                )}
              </TableRow>
            </TableHead>

            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8, border: 0 }}>
                    <Box sx={{ width: 44, height: 44, borderRadius: "10px", backgroundColor: C.surfaceAlt,
                      display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <Inventory2Icon sx={{ fontSize: 22, color: C.text3 }} />
                    </Box>
                    <Typography sx={{ fontSize: "0.875rem", color: C.text2, fontWeight: 500 }}>
                      {search ? `Tidak ada hasil untuk "${search}"` : "Belum ada barang di ruangan ini"}
                    </Typography>
                    <Typography sx={{ fontSize: "0.78rem", color: C.text3, mt: 0.5 }}>
                      {search ? "Coba kata kunci lain" : "Tambah barang dan atur ke ruangan ini"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : viewMode === "grouped" ? (
                /* ── Mode Dikelompokkan (Accordion) ── */
                groupedItems.map((group, idx) => {
                  const isExpanded = Boolean(expandedGroups[group.name]);

                  return (
                    <Box component="tbody" key={group.name} sx={{ display: "contents" }}>
                      {/* Baris Utama Kelompok Barang */}
                      <TableRow sx={{
                        backgroundColor: isExpanded ? "#F8FAFC" : "transparent",
                        "&:hover": { backgroundColor: isExpanded ? "#F1F5F9" : "#F8FAFC" },
                        borderBottom: `1px solid ${C.border}`,
                        transition: "background 0.12s",
                      }}>
                        <TableCell sx={{ ...tdSx, width: 60 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <IconButton size="small" onClick={() => toggleGroup(group.name)}
                              sx={{
                                width: 26, height: 26, borderRadius: "6px",
                                color: isExpanded ? C.primary : C.text3,
                                backgroundColor: isExpanded ? C.primaryBg : "transparent",
                                "&:hover": { backgroundColor: C.primaryBg, color: C.primary }
                              }}>
                              {isExpanded ? <KeyboardArrowDownIcon sx={{ fontSize: 18 }} /> : <KeyboardArrowRightIcon sx={{ fontSize: 18 }} />}
                            </IconButton>
                            <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                              {idx + 1}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Nama Barang */}
                        <TableCell sx={tdSx}>
                          <Box onClick={() => toggleGroup(group.name)} sx={{ cursor: "pointer", py: 0.25 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <Typography sx={{
                                fontSize: "0.875rem", fontWeight: 700,
                                color: isExpanded ? C.primary : C.text1,
                                "&:hover": { color: C.primary },
                              }}>
                                {group.name}
                              </Typography>
                              {group.category && (
                                <Chip size="small" label={group.category}
                                  sx={{ height: 20, fontSize: "0.68rem", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }} />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: "0.72rem", color: isExpanded ? C.primary : C.text3, mt: 0.25 }}>
                              {isExpanded ? "▲ Sembunyikan serial number" : `▼ Klik untuk melihat ${group.total_quantity} serial number`}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Total Unit */}
                        <TableCell sx={tdSx}>
                          <Box sx={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            px: 1.2, py: 0.5, borderRadius: "8px",
                            backgroundColor: C.primaryBg, border: `1px solid ${C.infoBdr}`
                          }}>
                            <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.primary, fontVariantNumeric: "tabular-nums" }}>
                              {group.total_quantity} {group.unit || "Unit"}
                            </Typography>
                          </Box>
                        </TableCell>

                        {/* Rentang Serial Number */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                            <Chip size="small" label={group.first_serial || "—"}
                              sx={{
                                fontFamily: "'JetBrains Mono', monospace",
                                fontSize: "0.72rem", fontWeight: 600,
                                backgroundColor: "#fff", border: `1px solid ${C.border}`,
                              }} />
                            {group.first_serial && group.last_serial && group.first_serial !== group.last_serial && (
                              <>
                                <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>s/d</Typography>
                                <Chip size="small" label={group.last_serial}
                                  sx={{
                                    fontFamily: "'JetBrains Mono', monospace",
                                    fontSize: "0.72rem", fontWeight: 600,
                                    backgroundColor: "#fff", border: `1px solid ${C.border}`,
                                  }} />
                              </>
                            )}
                          </Box>
                        </TableCell>

                        {/* Kondisi Summary */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {Object.entries(group.conditions || {}).map(([cond, count]) => (
                              <StatusChip key={cond} map={CONDITION_MAP} value={cond}
                                label={`${count} ${CONDITION_MAP[cond]?.label || cond}`} />
                            ))}
                          </Box>
                        </TableCell>

                        {/* Status Summary */}
                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                            {Object.entries(group.statuses || {}).map(([st, count]) => (
                              <StatusChip key={st} map={STATUS_MAP} value={st}
                                label={`${count} ${STATUS_MAP[st]?.label || st}`} />
                            ))}
                          </Box>
                        </TableCell>

                        {/* Tanggal Masuk */}
                        <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                          <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                            {formatDate(group.latest_date)}
                          </Typography>
                        </TableCell>

                        {/* Tombol Buka / Tutup */}
                        <TableCell sx={{ ...tdSx, textAlign: "right", pr: 2 }}>
                          <Button size="small" variant={isExpanded ? "contained" : "outlined"}
                            onClick={() => toggleGroup(group.name)}
                            endIcon={isExpanded
                              ? <KeyboardArrowDownIcon sx={{ fontSize: "14px !important" }} />
                              : <KeyboardArrowRightIcon sx={{ fontSize: "14px !important" }} />}
                            sx={{
                              textTransform: "none", fontSize: "0.72rem", fontWeight: 600,
                              borderRadius: "8px", py: 0.4, px: 1.2,
                              backgroundColor: isExpanded ? C.primary : "transparent",
                              color: isExpanded ? "#fff" : C.primary,
                              borderColor: isExpanded ? C.primary : C.infoBdr,
                              "&:hover": {
                                backgroundColor: isExpanded ? "#1D4ED8" : C.primaryBg,
                                borderColor: C.primary,
                              }
                            }}>
                            {isExpanded ? "Tutup" : `Lihat (${group.total_quantity})`}
                          </Button>
                        </TableCell>
                      </TableRow>

                      {/* Sub-Table Accordion yang Terbuka saat Nama Barang Diklik */}
                      <TableRow sx={{ backgroundColor: "#F8FAFC" }}>
                        <TableCell colSpan={8} sx={{ p: 0, borderBottom: isExpanded ? `1px solid ${C.border}` : 0 }}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ p: 2, pl: { xs: 2, md: 5 }, pr: 2, backgroundColor: "#F8FAFC", borderTop: `1px solid ${C.border}` }}>
                              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Typography sx={{ fontSize: "0.82rem", fontWeight: 700, color: C.text1 }}>
                                    📋 Daftar Serial Number: {group.name}
                                  </Typography>
                                  <Chip size="small" label={`${group.total_quantity} ${group.unit || "Unit"}`}
                                    sx={{ fontSize: "0.7rem", height: 22, backgroundColor: C.primaryBg, color: C.primary, fontWeight: 700 }} />
                                </Box>
                                <Typography sx={{ fontSize: "0.72rem", color: C.text3 }}>
                                  Klik tombol aksi untuk melihat riwayat atau memindahkan barang
                                </Typography>
                              </Box>

                              <TableContainer component={Paper} elevation={0}
                                sx={{ border: `1px solid ${C.border}`, borderRadius: "10px", backgroundColor: "#fff", maxHeight: 350, overflowY: "auto" }}>
                                <Table size="small">
                                  <TableHead>
                                    <TableRow sx={{ backgroundColor: C.surfaceAlt }}>
                                      <TableCell sx={{ ...thSx, width: 45, py: 1 }}>#</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Serial Number</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Spesifikasi</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Kondisi</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Status</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Tgl. Masuk</TableCell>
                                      <TableCell sx={{ ...thSx, py: 1 }}>Aksi</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {(group.items || []).map((subItem, sIdx) => (
                                      <TableRow key={subItem.id} sx={{
                                        "&:hover": { backgroundColor: "#F8FAFC" },
                                        "&:hover .sub-actions": { opacity: 1 },
                                        borderBottom: `1px solid ${C.border}`,
                                      }}>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600 }}>
                                            {sIdx + 1}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <Typography sx={{
                                            fontSize: "0.78rem", fontWeight: 600, color: C.text1,
                                            fontFamily: "'JetBrains Mono', monospace"
                                          }}>
                                            {subItem.serial_number || "—"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <Typography sx={{ fontSize: "0.75rem", color: C.text2 }}>
                                            {subItem.specification || "—"}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <StatusChip map={CONDITION_MAP} value={subItem.condition} />
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <StatusChip map={STATUS_MAP} value={subItem.status} />
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <Typography sx={{ fontSize: "0.75rem", color: C.text2 }}>
                                            {formatDate(subItem.latest_movement?.moved_at || subItem.purchase_date)}
                                          </Typography>
                                        </TableCell>
                                        <TableCell sx={{ ...tdSx, py: 0.75 }}>
                                          <Box className="sub-actions" sx={{ display: "flex", gap: 0.5, opacity: { xs: 1, md: 0.6 }, transition: "opacity 0.15s" }}>
                                            <Tooltip title="Lihat Detail Barang" arrow>
                                              <IconButton size="small"
                                                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${subItem.id}`)}
                                                sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3, "&:hover": { color: C.primary, backgroundColor: C.primaryBg } }}>
                                                <VisibilityIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Pindahkan Ruangan" arrow>
                                              <IconButton size="small"
                                                onClick={() => navigate(`/${window.location.pathname.split('/')[1]}/items/${subItem.id}/move`)}
                                                sx={{ width: 28, height: 28, borderRadius: "6px", color: C.text3, "&:hover": { color: C.success, backgroundColor: C.successBg } }}>
                                                <SwapHorizIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </Tooltip>
                                          </Box>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Box>
                  );
                })
              ) : (
                /* ── Mode Semua Baris (Flat View) ── */
                sortedFiltered.map((item, idx) => (
                  <TableRow key={item.id} sx={{
                    "&:hover": { backgroundColor: C.surfaceAlt },
                    borderBottom: `1px solid ${C.border}`,
                    transition: "background 0.12s",
                  }}>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.72rem", color: C.text3, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.75rem", color: C.text2,
                        fontFamily: "'JetBrains Mono', monospace", fontWeight: 500 }}>
                        {item.serial_number || <span style={{ color: C.text3 }}>—</span>}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: "0.825rem", fontWeight: 600, color: C.text1 }}>
                        {item.name}
                      </Typography>
                      {item.specification && (
                        <Tooltip title={item.specification} arrow placement="top">
                          <Typography noWrap sx={{ fontSize: "0.7rem", color: C.text3, mt: 0.2, maxWidth: 180 }}>
                            {item.specification}
                          </Typography>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: "center",
                        minWidth: 28, height: 22, px: 1,
                        borderRadius: "6px", backgroundColor: C.surfaceAlt, border: `1px solid ${C.border}` }}>
                        <Typography sx={{ fontSize: "0.78rem", fontWeight: 700, color: C.text1, fontVariantNumeric: "tabular-nums" }}>
                          {item.quantity ?? 1} {item.unit || "Unit"}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <StatusChip map={CONDITION_MAP} value={item.condition} />
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <StatusChip map={STATUS_MAP} value={item.status} />
                    </TableCell>
                    <TableCell sx={{ ...tdSx, whiteSpace: "nowrap" }}>
                      <Typography sx={{ fontSize: "0.78rem", color: C.text2 }}>
                        {formatDate(item.latest_movement?.moved_at || item.purchase_date)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Paper>
    </Box>
  );
}
